import { Form } from 'radix-ui'
import { useCallback, useMemo, useState } from 'react'
import './basic-player-information.scss'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import { PreviousButton } from '@Components/previous-button/previous-button'
import { Selector } from '@Components/selector/selector'
import { useHostConfig } from '../../../../hooks/use-host-config'
import { CLASS_NAME } from './constants'
import { PlayerInfoScreenProps, PlayerInfoFormData } from './types'
import { useAnnounce } from '../../../../hooks/use-announce'
import { Announcer } from '@Components/announcer/announcer'
import { AccessibleFormField } from '@Components/accessible-form-field/accessible-form-field'

export const PhoneInfoScreen = ({
  onSubmit,
  onBack,
  formData,
  rescheduleFlow,
}: PlayerInfoScreenProps) => {
  const [consentCheckbox, setConsentCheckbox] = useState<boolean>(false)
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false)
  const [numberOfTickets, setNumberOfTickets] = useState<number | undefined>(
    formData?.numberOfTickets,
  )
  const hostConfig = useHostConfig()
  const { message, announce } = useAnnounce()

  const dateOfBirthMax = useMemo(() => {
    const today = new Date()
    today.setFullYear(today.getFullYear() - hostConfig.playerInformation.minAgeForPhoneVerification)
    return today.toISOString().split('T')[0]
  }, [hostConfig.playerInformation.minAgeForPhoneVerification])

  const actionHandler = useCallback(
    (data: FormData) => {
      const formData: PlayerInfoFormData = {
        firstName: data.get('firstName')!.toString(),
        lastName: data.get('lastName')!.toString(),
        phoneNumber: data.get('phone')!.toString(),
        emailAddress: '',
        numberOfTickets: numberOfTickets ?? 1,
      }

      onSubmit(formData)
    },
    [onSubmit, numberOfTickets],
  )

  const formatPhoneNumber = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10)
    const match = digits.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)

    if (!match) {
      return ''
    }
    const [, area, prefix, line] = match
    if (line) {
      return `(${area}) ${prefix}-${line}`
    }
    if (prefix) {
      return `(${area}) ${prefix}`
    }
    if (area) {
      return `(${area}`
    }
    return ''
  }, [])

  const [phoneNumberValue, setPhoneNumberValue] = useState(
    formData ? formatPhoneNumber(formData.phoneNumber) : '',
  )

  const phoneNumberChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setPhoneNumberValue(formatPhoneNumber(value))
    },
    [formatPhoneNumber],
  )

  return (
    <Form.Root asChild action={actionHandler}>
      <Form.Form className={CLASS_NAME}>
        <Announcer message={message} className={`${CLASS_NAME}__sr-alert`} />

        <section className={`${CLASS_NAME}__input-row`}>
          <AccessibleFormField
            name="firstName"
            label="First Name*"
            className={`${CLASS_NAME}__field`}
            labelClassName={`${CLASS_NAME}__label`}
            messageClassName={`${CLASS_NAME}__message`}
            inputClassName={`${CLASS_NAME}__input-control`}
            required
            announce={announce}
            messages={{ valueMissing: 'Please enter your first name' }}>
            {(inputProps) => (
              <input
                {...inputProps}
                type="text"
                placeholder="First Name"
                autoComplete="given-name"
                defaultValue={formData?.firstName}
                readOnly={rescheduleFlow}
              />
            )}
          </AccessibleFormField>

          <AccessibleFormField
            name="lastName"
            label="Last Name*"
            className={`${CLASS_NAME}__field`}
            labelClassName={`${CLASS_NAME}__label`}
            messageClassName={`${CLASS_NAME}__message`}
            inputClassName={`${CLASS_NAME}__input-control`}
            required
            announce={announce}
            messages={{ valueMissing: 'Please enter your last name' }}>
            {(inputProps) => (
              <input
                {...inputProps}
                type="text"
                placeholder="Last Name"
                autoComplete="family-name"
                defaultValue={formData?.lastName}
                readOnly={rescheduleFlow}
              />
            )}
          </AccessibleFormField>
        </section>

        <AccessibleFormField
          name="phone"
          label="Phone Number*"
          className={`${CLASS_NAME}__field`}
          labelClassName={`${CLASS_NAME}__label`}
          messageClassName={`${CLASS_NAME}__message`}
          inputClassName={`${CLASS_NAME}__input-control`}
          required
          announce={announce}
          messages={{
            valueMissing: 'Please enter your phone number',
            patternMismatch: 'Please enter a valid phone number (XXX) XXX-XXXX',
          }}>
          {(inputProps) => (
            <input
              {...inputProps}
              type="tel"
              placeholder="(123) 456-7890"
              autoComplete="tel"
              inputMode="numeric"
              pattern="\(\d{3}\) \d{3}-\d{4}"
              maxLength={14}
              value={phoneNumberValue}
              onChange={phoneNumberChangeHandler}
              readOnly={rescheduleFlow}
            />
          )}
        </AccessibleFormField>

        {rescheduleFlow ? null : (
          <AccessibleFormField
            name="dateOfBirth"
            label="Date of Birth*"
            className={`${CLASS_NAME}__field`}
            labelClassName={`${CLASS_NAME}__label`}
            messageClassName={`${CLASS_NAME}__message`}
            inputClassName={`${CLASS_NAME}__input-control`}
            required
            announce={announce}
            messages={{
              valueMissing: 'Please enter your date of birth',
              rangeOverflow: hostConfig.playerInformation.ageForVerificationError,
            }}>
            {(inputProps) => (
              <input
                {...inputProps}
                type="date"
                autoComplete="bday"
                max={dateOfBirthMax}
                placeholder="mm/dd/yyyy"
                readOnly={rescheduleFlow}
              />
            )}
          </AccessibleFormField>
        )}

        {rescheduleFlow ? (
          <Form.Field name="numberOfTickets">
            <Form.Label className={`${CLASS_NAME}__label`}>Number of Tickets</Form.Label>

            <Form.Control name="numberOfTickets" asChild>
              <Selector<number>
                ariaLabel={'Number of tickets selector'}
                placeHolderText={''}
                value={numberOfTickets ? `${numberOfTickets}` : undefined}
                selectables={Array.from({ length: 15 }, (_, i) => {
                  const value = i + 1
                  return {
                    value,
                    label: value === 15 ? '15+' : value.toString(),
                  }
                })}
                onValueChange={setNumberOfTickets}
              />
            </Form.Control>
          </Form.Field>
        ) : null}

        {rescheduleFlow ? null : (
          <Form.Field name="consentCheckbox" className={`${CLASS_NAME}__consent-checkbox`}>
            <Form.Control asChild>
              <input
                type="checkbox"
                name="consentCheckbox"
                role="checkbox"
                aria-checked={consentCheckbox}
                onChange={(e) => setConsentCheckbox(e.target.checked)}
                required
              />
            </Form.Control>
            <Form.Label>{hostConfig.playerInformation.phoneConsentLabel}</Form.Label>
          </Form.Field>
        )}
        {rescheduleFlow ? null : (
          <Form.Field name="agreeToTerms" className={`${CLASS_NAME}__consent-checkbox`}>
            <Form.Control asChild>
              <input
                type="checkbox"
                name="agreeToTerms"
                role="checkbox"
                aria-checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                required
              />
            </Form.Control>
            <Form.Label>{hostConfig.playerInformation.agreeToTermsLabel}</Form.Label>
          </Form.Field>
        )}
        <FormFloatingFooter>
          <section className={`floating-footer__buttons`}>
            <PreviousButton onClick={onBack} />
            <Button
              type="submit"
              className={`floating-footer__next-button`}
              disabled={rescheduleFlow ? false : !consentCheckbox || !agreeToTerms}>
              Next
            </Button>
          </section>
        </FormFloatingFooter>
      </Form.Form>
    </Form.Root>
  )
}
